import { Controller } from "@hotwired/stimulus"

// Manages device form state, validation, and user interactions
export default class extends Controller {
  static targets = ["addButton", "clearButton", "saveButton", "deviceTypeId", "deviceModelId", "placement", "powerSupplyId"]
  static values = {
    projectId: String,
    floorPlanId: String,
    deviceModelsMap: Object,  // All device models by type
    accessPointTypeIds: Array,
    switchSpecTypeIds: Array
  }

  connect() {

    this.currentMode = 'add'
    this.isPopulatingForm = false
    this.pendingFormData = null
    this.lastValidationResult = null
    this.currentDeviceId = null

    // Make this controller instance accessible for cross-controller validation
    this.element.deviceFormController = this

    // Setup event listeners for cross-controller communication
    this.setupEventListeners()

    // Setup form field change listeners
    this.setupFormFieldListeners()

    // Initialize add mode state with default values
    this.addModeFormState = {
      deviceTypeId: '',
      deviceModelId: '',
      placement: '',
      powerSupplyId: '',
      powerPercentage: '80'
    }

    // Initialize previous device type for change tracking
    this.previousDeviceTypeId = ''

    this.bindFormEvents()
    this.updateButtonStates()
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('device:placement:deviceSelected', this.handleDeviceSelected)
    document.removeEventListener('device:placement:modeChanged', this.handleModeChanged)
    document.removeEventListener('device:placement:devicePositionUpdated', this.handleDevicePositionUpdated)
    document.removeEventListener('device:placement:validationFailed', this.handleValidationFailed)

    // Clean up form event listener
    if (this.element) {
      this.element.removeEventListener('change', this.onFormChange)
    }
  }

  // Button click handlers
  addDevice(event) {
    event.preventDefault()

    if (!this.validateForm()) {
      return
    }


    const formData = this.extractFormData()

    // Save the successful add state for future use (excluding position data)
    this.addModeFormState = {
      deviceTypeId: formData.deviceTypeId,
      deviceModelId: formData.deviceModelId,
      placement: formData.placement,
      powerSupplyId: formData.powerSupplyId,
      powerPercentage: formData.powerPercentage
    }

    this.dispatch('addNew', formData)
  }

  clearDevice(event) {
    event.preventDefault()
    this.dispatch('clear')
  }

  saveDevices(event) {
    event.preventDefault()
    this.dispatch('save')
  }

  // Form validation: required fields depend on device type.
  validateForm(mode = 'add') {
    // Base required fields for all device types
    const requiredFields = [
      { fieldName: 'device_type_id', name: 'Device Type' },
      { fieldName: 'device_model_id', name: 'Device Model' }
    ]

    // Get device type to determine additional required fields
    const deviceTypeName = this.getDeviceTypeName()

    // Check if Access Point sections are visible (backup check)
    const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
    const isAccessPointVisible = accessPointSection && accessPointSection.style.display !== 'none'

    // Only Access Points require placement and power supply
    if (deviceTypeName === "Access Point" || isAccessPointVisible) {
      requiredFields.push(
        { fieldName: 'placement', name: 'Placement' },
        { fieldName: 'power_supply_id', name: 'Power Supply' }
      )
    }

    let isValid = true
    const invalidFields = []

    // Clear all previous errors
    this.clearValidationErrors()

    requiredFields.forEach((field) => {
      const value = this.getDropdownValue(field.fieldName)

      if (!value || value.trim() === '' || value === 'undefined') {
        isValid = false
        invalidFields.push(field.name)
        this.showValidationError(field.fieldName)
      }
    })

    // If validation failed and we have conditional fields in error, ensure sections are visible
    if (!isValid && invalidFields.some(field => field.includes('Placement') || field.includes('Power Supply'))) {
      this.ensureConditionalSectionsVisible(deviceTypeName)
    }

    // Store validation result for other methods to check
    this.lastValidationResult = { isValid, invalidFields, mode }

    return isValid
  }

  // Simplified form data extraction with reliable Stimulus targets
  extractFormData() {

    const extractedData = {
      deviceTypeId: this.getDropdownValue('device_type_id'),
      deviceModelId: this.getDropdownValue('device_model_id'),
      placement: this.getDropdownValue('placement'),
      powerSupplyId: this.getDropdownValue('power_supply_id'),
      powerPercentage: document.getElementById('power_percentage_slider')?.value || 80
    }

    return extractedData
  }

  // Helper to get current device data for integrity protection
  getCurrentDeviceData() {
    if (!this.currentDeviceId) return null

    // Try to get data from placement controller
    const placementController = this.getPlacementController()
    if (placementController && placementController.devices) {
      const device = placementController.devices.get(this.currentDeviceId)
      if (device) {
        return device
      }
    }

    return null
  }

  // Setup form field change listeners for dropdown cascading behavior
  setupFormFieldListeners() {

    // Listen for device type changes to reset device model
    const deviceTypeInput = document.querySelector('#device_type_id [data-simplified-dropdown-target="valueInput"]')
    if (deviceTypeInput) {
      deviceTypeInput.addEventListener('change', this.handleDeviceTypeChange.bind(this))
    }

    // Listen for device model changes to refresh specs display (PoE switch/Gateway)
    const deviceModelInput = document.querySelector('#device_model_id [data-simplified-dropdown-target="valueInput"]')
    if (deviceModelInput) {
      deviceModelInput.addEventListener('change', () => {
        const typeId = this.getDropdownValue('device_type_id')
        this.populateSpecs(typeId)
        this.updatePowerSupplyOptions()

        const typeName = (this.getDeviceTypeNameFromId(typeId) || "").toLowerCase()
        if (typeName.includes('access point') || typeName === 'ap') {
          this.setDropdownValue('power_supply_id', '', 'Select value')
        }
      })
    }
  }

  // Handle device type changes - reset device model when type changes
  handleDeviceTypeChange(event) {
    const newDeviceTypeId = event.target.value
    const previousDeviceTypeId = this.previousDeviceTypeId || ''


    // Skip if form is being populated programmatically
    if (this.isPopulatingForm) {
      return
    }

    // Always reset device model when type changes (essential for data consistency)
    const deviceModelController = this.getDropdownController('device_model_id')
    if (deviceModelController) {
      deviceModelController.clearSelection()
    }

    // Load new device models for the selected type
    if (newDeviceTypeId) {
      this.loadDeviceModelsSync(newDeviceTypeId)

      // Update conditional sections
      this.updateConditionalSectionsByTypeId(newDeviceTypeId)
      this.updatePowerSupplyOptions()

      // UX improvement: in add mode, only clear conditional fields if type actually changed
      // This preserves user configuration when switching back to the same type
      const isTypeActuallyChanged = previousDeviceTypeId !== newDeviceTypeId
      const shouldClearConditionalFields = this.currentMode === 'edit' || isTypeActuallyChanged

      if (shouldClearConditionalFields) {
        this.clearConditionalFields()
      } else {
      }

      // If in edit mode, update the device data immediately
      if (this.currentMode === 'edit' && this.currentDeviceId) {
        this.updateDeviceInMemory({
          deviceTypeId: newDeviceTypeId,
          deviceModelId: null,
          placement: null,
          powerSupplyId: null,
          power_supply_id: null  // Also clear snake_case version
        })
      }
    } else {
      // If no device type selected, hide all conditional sections
      this.hideAllConditionalSections()
      this.clearConditionalFields()
    }

    // Remember this type for next change comparison
    this.previousDeviceTypeId = newDeviceTypeId
  }

  // Helper to get dropdown controller
  getDropdownController(fieldName) {
    const fieldContainer = document.getElementById(fieldName)
    if (fieldContainer) {
      return this.application.getControllerForElementAndIdentifier(fieldContainer, 'simplified-dropdown')
    }
    return null
  }

  // Clear conditional fields (placement, power supply, etc.)
  clearConditionalFields() {

    const conditionalFields = ['placement', 'power_supply_id']
    conditionalFields.forEach(fieldName => {
      const controller = this.getDropdownController(fieldName)
      if (controller) {
        controller.clearSelection()
      }
    })

    // Reset power slider to default
    this.updatePowerSliderUI(100)
  }

  // Hide all conditional sections
  hideAllConditionalSections() {

    const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
    if (accessPointSection) {
      accessPointSection.style.display = 'none'
    }

    const specsSection = document.querySelector('[data-device-target="specsSection"]')
    if (specsSection) {
      specsSection.style.display = 'none'
    }
  }

  // Update device data in memory (for edit mode)
  updateDeviceInMemory(updates) {
    if (!this.currentDeviceId) return

    const placementController = this.getPlacementController()
    if (placementController && placementController.devices) {
      const device = placementController.devices.get(this.currentDeviceId)
      if (device) {
        Object.assign(device, updates)

        // Dispatch device update event
        placementController.dispatch('deviceUpdated', {
          deviceId: this.currentDeviceId,
          deviceData: device
        })
      }
    }
  }

  // Clean up event listeners handled in setupEventListeners()
  // Note: Event listeners are cleaned up in the main disconnect() method above

  // Enter edit mode and populate form with device data
  enterEditMode(deviceId, deviceData) {

    this.currentMode = 'edit'
    this.currentDeviceId = deviceId
    this.isPopulatingForm = true

    // Clear any previous validation errors
    this.clearValidationErrors()

    // Populate form with device data
    this.populateFormWithDeviceData(deviceData)

    this.isPopulatingForm = false
  }

  // Enter add mode and preserve form state
  enterAddMode() {

    this.currentMode = 'add'
    this.currentDeviceId = null

    // Clear any validation errors
    this.clearValidationErrors()

    // UX improvement: don't reset form to preserve user's configuration
    // The form state will be restored by handleModeChanged() if available
    // This allows users to add multiple devices with the same configuration

  }

  // Populate form with existing device data
  populateFormWithDeviceData(deviceData) {

    // Set device type
    if (deviceData.deviceTypeId) {
      this.setDropdownValue('device_type_id', deviceData.deviceTypeId)

      // Load device models for this type
      this.loadDeviceModelsSync(deviceData.deviceTypeId)

      // Set device model after models are loaded
      if (deviceData.deviceModelId) {
        setTimeout(() => {
          this.setDropdownValue('device_model_id', deviceData.deviceModelId)
        }, 100)
      }
    }

    // Set conditional fields if it's an Access Point
    if (deviceData.deviceTypeId === '1' || deviceData.deviceTypeId === 1) {
      // Show Access Point sections
      const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
      if (accessPointSection) {
        accessPointSection.style.display = 'block'
      }

      if (deviceData.placement) {
        this.setDropdownValue('placement', deviceData.placement)
      }
      if (deviceData.powerSupplyId) {
        this.setDropdownValue('power_supply_id', deviceData.powerSupplyId)
      }
    } else {
      // Hide Access Point sections for other device types
      const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
      if (accessPointSection) {
        accessPointSection.style.display = 'none'
      }
    }

  }

  // Reset form to clean add state
  resetFormToAddState() {

    // Clear all dropdowns
    this.setDropdownValue('device_type_id', '', 'Select value')
    this.setDropdownValue('device_model_id', '', 'Select value')
    this.setDropdownValue('placement', '', 'Select value')
    this.setDropdownValue('power_supply_id', '', 'Select value')

    // Hide conditional sections
    const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
    if (accessPointSection) {
      accessPointSection.style.display = 'none'
    }


  }

  // Helper to get placement controller instance
  getPlacementController() {
    const placementElement = document.querySelector('[data-controller*="device-placement"]')
    return placementElement ? placementElement.devicePlacementController : null
  }

  // Simplified and reliable dropdown value reading (supports SimplifiedDropdownComponent).
  getDropdownValue(fieldName) {

    try {
      // First try SimplifiedDropdownComponent structure
      const fieldContainer = document.getElementById(fieldName)
      if (fieldContainer) {
        // Check if it's SimplifiedDropdownComponent
        const valueInput = fieldContainer.querySelector('[data-simplified-dropdown-target="valueInput"]')
        if (valueInput) {
          const value = valueInput.value
          return value || null
        }
      }

      // Fallback to legacy system for gradual migration
      return this.legacyGetDropdownValue(fieldName)

    } catch (error) {

      return null
    }
  }

  // Legacy fallback (will be removed after migration)
  legacyGetDropdownValue(fieldName) {
    // First try direct hidden input access (works for SimplifiedDropdownComponent too)
    const hiddenInput = document.querySelector(`input[name="device[${fieldName}]"][type="hidden"]`)
    if (hiddenInput && hiddenInput.value) {
      return hiddenInput.value
    }

    // Try by ID (for direct field access)
    const hiddenField = document.getElementById(fieldName)
    if (hiddenField && hiddenField.value) {
      return hiddenField.value
    }

    // Legacy radio button approach
    const radioButtons = document.querySelectorAll(`input[name="device[${fieldName}]"]:checked`)
    if (radioButtons.length > 0) {
      return radioButtons[0].value
    }


    return null
  }

  // Simplified and reliable dropdown value setting (supports SimplifiedDropdownComponent).
  setDropdownValue(fieldName, value, displayText = null) {

    try {
      // First try SimplifiedDropdownComponent structure
      const fieldContainer = document.getElementById(fieldName)
      if (fieldContainer) {
        // Check if it's SimplifiedDropdownComponent
        const valueInput = fieldContainer.querySelector('[data-simplified-dropdown-target="valueInput"]')
        if (valueInput) {
          // Set the hidden input value
          valueInput.value = value || ''

          // Get the dropdown controller and update display
          const dropdownController = this.application.getControllerForElementAndIdentifier(
            fieldContainer,
            'simplified-dropdown'
          )

          if (dropdownController) {
            let finalDisplayText = displayText

            // Auto-generate display text if not provided
            if (!finalDisplayText && value) {
              finalDisplayText = this.getDisplayTextForField(fieldName, value)
            }

            // Use placeholder if still no display text
            if (!finalDisplayText) {
              finalDisplayText = this.getPlaceholderForField(fieldName)
            }

            // Update the dropdown display
            dropdownController.updateValue(value, finalDisplayText)
          } else {

          }

          // Trigger change event for form integration
          valueInput.dispatchEvent(new Event('change', { bubbles: true }))
          return true
        }
      }

      // Fallback to legacy system
      return this.legacySetDropdownValue(fieldName, value, displayText)

    } catch (error) {

      return false
    }
  }

  // Get placeholder text for fields.
  getPlaceholderForField(fieldName) {
    switch (fieldName) {
      case 'device_type_id': return 'Select device type'
      case 'device_model_id': return 'Select model'
      case 'placement': return 'Select placement'
      case 'power_supply_id': return 'Select power supply'
      default: return 'Select value'
    }
  }

  // Helper method to get display text for a field value
  getDisplayTextForField(fieldName, value) {
    if (!value) return null

    switch (fieldName) {
      case 'device_type_id':
        // Look up device type name
        return this.getDeviceTypeNameFromId(value)

      case 'device_model_id':
        // Look up device model name from preloaded data
        if (this.hasDeviceModelsMapValue) {
          for (const typeId of Object.keys(this.deviceModelsMapValue)) {
            const models = this.deviceModelsMapValue[typeId]
            const model = models.find(m => m.value.toString() === value.toString())
            if (model) {
              return model.name
            }
          }
        }
        break

      case 'placement':
        // Convert placement enum to display text
        return value.charAt(0).toUpperCase() + value.slice(1)

      case 'power_supply_id':
        // Look up power supply name from dropdown options
        const powerSupplyDropdown = document.querySelector('#power_supply_id')
        if (powerSupplyDropdown) {
          const option = powerSupplyDropdown.querySelector(`[data-value="${value}"]`)
          if (option) {
            return option.textContent.trim()
          }
        }

        // Fallback static mapping
        const powerSupplyMap = {
          '1': 'PoE',
          '2': 'Power adapter'
        }
        return powerSupplyMap[value.toString()]
    }

    return null
  }

  // Legacy fallback (will be removed after migration)
  legacySetDropdownValue(fieldName, value, displayText = null) {
    const hiddenField = document.getElementById(fieldName)
    if (hiddenField) {
      hiddenField.value = value || ''

      // Try to update legacy dropdown display if available
      if (displayText) {
        const displayElement = document.querySelector(`#${fieldName}_dropdown_input_value`)
        if (displayElement) {
          displayElement.textContent = displayText
        }
      }

      return true
    }


    return false
  }

  // Helper to convert display text back to value (fallback strategy)
  getValueFromDisplayText(fieldName, displayText) {
    // This is a fallback - in production you'd want a proper mapping
    // For now, just return null to prevent corruption, but log the attempt
    return null
  }

  // Get device type name (supports SimplifiedDropdownComponent).
  getDeviceTypeName() {
    const deviceTypeId = this.getDropdownValue('device_type_id')
    if (!deviceTypeId) return null

    // First try SimplifiedDropdownComponent structure
    const simplifiedDisplayText = document.querySelector('#device_type_id [data-simplified-dropdown-target="displayText"]')
    if (simplifiedDisplayText && simplifiedDisplayText.textContent.trim() !== 'Select value' && simplifiedDisplayText.textContent.trim() !== 'Select device type') {
      return simplifiedDisplayText.textContent.trim()
    }

    // Try to get from SimplifiedDropdownComponent options
    const dropdown = document.querySelector('#device_type_id')
    if (dropdown) {
      const option = dropdown.querySelector(`[data-value="${deviceTypeId}"]`)
      if (option) {
        return option.textContent.trim()
      }
    }

    // Legacy DropdownInputComponent support
    const legacyDisplayElement = document.querySelector('#device_type_id_dropdown_input_value')
    if (legacyDisplayElement && legacyDisplayElement.textContent.trim() !== 'Select value') {
      return legacyDisplayElement.textContent.trim()
    }

    // Fallback: Map device type ID to name (based on common values)
    const typeMap = {
      '1': 'Access Point',
      '2': 'Switch',
      '3': 'Gateway'
    }

    if (typeMap[deviceTypeId]) {
      return typeMap[deviceTypeId]
    }


    return null
  }

  clearValidationErrors() {
    // Clear all error classes from SimplifiedDropdownComponent containers
    document.querySelectorAll('.input.input--error[data-controller*="simplified-dropdown"]').forEach(element => {
      element.classList.remove('input--error')
    })

    // Clear all error classes from legacy dropdown containers
    document.querySelectorAll('.input__dropdown.input--error').forEach(element => {
      element.classList.remove('input--error')
    })
  }

  // Ensure conditional sections are visible on validation failure.
  ensureConditionalSectionsVisible(deviceTypeName) {
    if (deviceTypeName === "Access Point") {
      const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
      if (accessPointSection) {
        accessPointSection.style.display = 'block'
      }
    }
  }

  // Check if we can safely leave edit mode
  canSafelyLeaveEditMode() {

    // If in add mode, always safe to leave
    if (this.currentMode === 'add') {
      return true
    }

    // In edit mode, we need to check if form data is valid
    // But only validate if user has made changes
    if (this.currentMode === 'edit') {

      // Skip validation if form is being populated programmatically
      if (this.isPopulatingForm) {
        return true
      }

      // Validate current form state
      const isValid = this.validateForm('edit')

      if (!isValid) {
        // Show visual feedback for invalid fields
        this.highlightInvalidFields()
        return false
      }

      return true
    }

    // Default: safe to leave
    return true
  }

  // Static method to check validation from other controllers - synchronous approach
  static canLeaveCurrentDevice() {
    // Find the form controller instance
    const formControllerElement = document.querySelector('[data-controller*="device-form"]')
    if (formControllerElement && formControllerElement.deviceFormController) {
      return formControllerElement.deviceFormController.canSafelyLeaveEditMode()
    }

    // If no form controller found, assume it's safe to leave

    return true
  }

  highlightInvalidFields() {
    // Flash the error fields to draw attention
    document.querySelectorAll('.input--error').forEach(element => {
      element.style.animation = 'shake 0.5s'
      setTimeout(() => {
        element.style.animation = ''
      }, 500)
    })
  }

  showValidationError(fieldName) {


    // Find the field container (both SimplifiedDropdownComponent and legacy support)
    const fieldContainer = document.getElementById(fieldName)

    if (fieldContainer) {
      // Check if it's SimplifiedDropdownComponent or legacy DropdownInputComponent
      const isSimplifiedDropdown = fieldContainer.querySelector('[data-simplified-dropdown-target="optionsPanel"]')

      // Check if field is in a conditional section
      const isInConditionalSection = fieldContainer.closest('[data-device-target="accessPointSections"]')
      if (isInConditionalSection) {

      }

      if (isSimplifiedDropdown) {
        // SimplifiedDropdownComponent: add error class to the main .input container
        if (fieldContainer.classList.contains('input')) {
          fieldContainer.classList.add('input--error')


          // Additional check for conditional fields
          if (isInConditionalSection) {

          }
        } else {

        }
      } else {
        // Legacy DropdownInputComponent support
        const dropdownElement = fieldContainer.querySelector('.input__dropdown')
        if (dropdownElement) {
          dropdownElement.classList.add('input--error')

        } else {

        }
      }
    } else {

    }
  }

  // Button state management
  updateButtonStates() {
    if (this.currentMode === 'add') {
      this.enableAddButton()
      this.disableClearButton()
    } else {
      this.disableAddButton()
      this.enableClearButton()
    }
  }

  enableAddButton() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.removeAttribute('disabled')
    }
  }

  disableAddButton() {
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.setAttribute('disabled', 'disabled')
    }
  }

  enableClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = false
      this.clearButtonTarget.removeAttribute('disabled')
    }
  }

  disableClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = true
      this.clearButtonTarget.setAttribute('disabled', 'disabled')
    }
  }

  setupEventListeners() {
    // Handle events from device-placement-controller
    this.handleDeviceSelected = (event) => {
      const { deviceId, deviceData, skipFormUpdate } = event.detail

      // Debug: device selected

      const isDeviceSwitching = this.currentMode === 'edit' // We're already in edit mode, switching between devices

      // Update the tracking ID
      this.currentDeviceId = deviceId  // Fix: use currentDeviceId for selection tracking
      this.currentMode = 'edit'
      this.updateButtonStates()

      // Only populate form if not asked to skip (e.g., during validation failure recovery)
      if (!skipFormUpdate) {
        // Pre-validation is now handled in PlacementController.selectDevice()
        // This event handler now only needs to update the form
        this.populateFormWithDevice(deviceData, isDeviceSwitching)

        // Also update add mode state when editing (useful if device is cleared later)
        const cleanDeviceState = { ...deviceData }
        delete cleanDeviceState.id
        delete cleanDeviceState.originalId
        delete cleanDeviceState.isExisting
        delete cleanDeviceState.markedForDeletion
        delete cleanDeviceState.positionX
        delete cleanDeviceState.positionY
        this.addModeFormState = cleanDeviceState
      } else {
      }
    }

    this.handleAttemptModeChange = (event) => {
      const { newMode, currentMode, currentDeviceId } = event.detail

      // Check if we can safely leave current mode
      if (currentMode === 'edit' && !this.canSafelyLeaveEditMode()) {

        // Notify other controllers to restore the selection since validation failed
        const deviceToRestore = this.currentDeviceId || currentDeviceId
        if (deviceToRestore) {
          this.dispatch('validationFailedRestoreSelection', {
            deviceId: deviceToRestore,
            invalidFields: this.lastValidationResult?.invalidFields || []
          })
        }
        return
      }

      // Validation passed, notify placement controller to proceed
      this.dispatch('modeChangeApproved', { mode: newMode })
    }

    this.handleModeChanged = (event) => {
      const { mode, restoreState } = event.detail

      this.currentMode = mode
      this.currentDeviceId = null // Clear editing device when switching to add mode
      this.updateButtonStates()

      if (mode === 'add') {
        if (restoreState) {
          // If a state was passed (e.g., from clearing a device), use it
          this.addModeFormState = restoreState
          this.populateFormWithDevice(this.addModeFormState)
        } else if (this.addModeFormState && Object.keys(this.addModeFormState).length > 0) {
          // Restore the last known add state if available
          this.populateFormWithDevice(this.addModeFormState)
        } else {
          // UX improvement: if no saved state, preserve current form content
          // This allows users to continue with their current configuration

          // Still ensure conditional sections are properly displayed for current type
          const currentTypeId = this.getDropdownValue('device_type_id')
          if (currentTypeId) {
            this.updateConditionalSectionsByTypeId(currentTypeId)
          }
        }
      }
    }

    this.handleDevicePositionUpdated = (event) => {
      const { positionX, positionY } = event.detail
      this.setFieldValue('position_x', positionX)
      this.setFieldValue('position_y', positionY)
    }

    // Handle validation failure from placement controller save
    this.handleValidationFailed = (event) => {
      const { errors } = event.detail

      // Show validation errors visually
      this.clearValidationErrors()

      // Check current form and highlight errors
      const formData = this.extractFormData()
      if (!formData.deviceTypeId) {
        this.showValidationError('device_type_id')
      }
      if (!formData.deviceModelId) {
        this.showValidationError('device_model_id')
      }

      // For Access Point, check additional fields
      if (formData.deviceTypeId === '1' || formData.deviceTypeId === 1) {
        if (!formData.placement) {
          this.showValidationError('placement')
        }
        if (!formData.powerSupplyId) {
          this.showValidationError('power_supply_id')
        }
      }

      console.error('Cannot save devices due to validation errors:', errors)
    }

    // Register event listeners
    document.addEventListener('device:placement:deviceSelected', this.handleDeviceSelected)
    document.addEventListener('device:placement:modeChanged', this.handleModeChanged)
    document.addEventListener('device:placement:attemptModeChange', this.handleAttemptModeChange)
    document.addEventListener('device:placement:devicePositionUpdated', this.handleDevicePositionUpdated)
    document.addEventListener('device:placement:validationFailed', this.handleValidationFailed)
  }

  bindFormEvents() {
    // Listen for form changes to sync with device data
    this.onFormChange = this.onFormChange.bind(this)
    this.element.addEventListener('change', this.onFormChange)

    // Add immediate tracking for critical dropdown changes
    this.setupImmediateChangeTracking()
  }

  setupImmediateChangeTracking() {
    // Track device type changes immediately using Stimulus targets
    if (typeof this.hasTarget === 'function' && this.hasTarget('deviceTypeId')) {
      this.deviceTypeIdTarget.addEventListener('change', (event) => {
        if (!this.isPopulatingForm) {
          this.handleImmediateDeviceTypeChange(event.target.value)
        }
      })
    } else {
      // Fallback to traditional ID lookup
      const deviceTypeDropdown = document.querySelector('input[name*="device_type_id"]')
      if (deviceTypeDropdown) {
        deviceTypeDropdown.addEventListener('change', (event) => {
          if (!this.isPopulatingForm) {
            this.handleImmediateDeviceTypeChange(event.target.value)
          }
        })
      }
    }

    // Track radio button selections for immediate feedback
    document.addEventListener('change', (event) => {
      if (!this.isPopulatingForm && this.currentMode === 'edit' &&
        event.target.type === 'radio' && event.target.name &&
        event.target.name.includes('device[')) {

        const fieldName = event.target.name.match(/device\[([^\]]+)\]/)?.[1]
        if (fieldName) {
          this.handleImmediateFieldChange(fieldName, event.target.value)
        }
      }
    })
  }

  handleImmediateDeviceTypeChange(newDeviceTypeId) {

    // Load device models regardless of mode (add or edit)
    this.loadDeviceModelsSync(newDeviceTypeId)

    // Update conditional sections (show/hide placement, power supply, etc.)
    this.updateConditionalSectionsByTypeId(newDeviceTypeId)

    if (this.currentDeviceId) {
      // Get current device data and update immediately (edit mode)
      const placementController = this.getPlacementController()
      if (placementController && placementController.devices) {
        const device = placementController.devices.get(this.currentDeviceId)
        if (device) {

          device.deviceTypeId = String(newDeviceTypeId)

          // Clear device model when type changes
          device.deviceModelId = null
          this.setDropdownValueReliably('device_model_id', '')

          // Trigger icon update immediately
          placementController.dispatch('deviceUpdated', {
            deviceId: this.currentDeviceId,
            deviceData: device
          })


        }
      }
    } else {
      // Add mode: update add mode form state
      this.addModeFormState.deviceTypeId = newDeviceTypeId
      this.addModeFormState.deviceModelId = '' // Clear model when type changes

    }
  }

  handleImmediateFieldChange(fieldName, newValue) {


    if (this.currentDeviceId) {
      const placementController = this.getPlacementController()
      if (placementController && placementController.devices) {
        const device = placementController.devices.get(this.currentDeviceId)
        if (device) {

          device[fieldName] = newValue

          // For significant changes, trigger device update
          if (['placement', 'deviceModelId', 'powerSupplyId'].includes(fieldName)) {
            placementController.dispatch('deviceUpdated', {
              deviceId: this.currentDeviceId,
              deviceData: device
            })
          }


        }
      }
    }
  }

  onFormChange(event) {
    // Prevent infinite loops during form population
    if (this.isPopulatingForm) {

      return
    }

    // Additional protection: ignore change events that happen too soon after form population
    if (this.lastFormPopulationTime && (Date.now() - this.lastFormPopulationTime) < 200) {

      return
    }

    // Only dispatch change event if we are in edit mode
    if (this.currentMode === 'edit') {


      const updatedData = this.extractFormData()

      // Verify that the data is reasonable before dispatching
      const hasValidData = updatedData.deviceTypeId !== null ||
        updatedData.deviceModelId !== null ||
        updatedData.placement !== null ||
        updatedData.powerSupplyId !== null

      if (hasValidData) {

        this.dispatch('changed', updatedData)
      } else {

      }
    }
  }

  // Form data population methods - SIMPLIFIED and SYNCHRONOUS
  populateFormWithDevice(deviceData, isDeviceSwitching = false) {

    this.isPopulatingForm = true

    try {
      // 1. Clear the form to a clean state
      this.clearFormForPopulation()


      // Verify form is actually cleared
      const typeValue = document.getElementById('device_type_id')?.value
      const modelValue = document.getElementById('device_model_id')?.value

      // 2. Set basic form fields first (these should always work)
      this.setBasicFormFields(deviceData)

      // 3. Update Device Type first (following original pattern)
      if (deviceData.deviceTypeId) {
        this.setFieldValue('device_type_id', deviceData.deviceTypeId)
        this.updateDropdownSelection('device_type_id', deviceData.deviceTypeId)

        // 4. Clear device model selection before loading new options (like original)
        this.clearDeviceModelSelection()

        // 5. SYNCHRONOUSLY load device models from preloaded data
        try {
          this.loadDeviceModelsSync(deviceData.deviceTypeId)

          // Set device model immediately
          this.setDropdownValueReliably('device_model_id', deviceData.deviceModelId)

        } catch (modelError) {
        }
      } else {
      }

      // 7. Set position fields
      this.setFieldValue('position_x', deviceData.positionX)
      this.setFieldValue('position_y', deviceData.positionY)

      // 8. Update conditional sections for device type (following original pattern)
      this.updateConditionalSectionsDirectly(deviceData)

      // 9. Set conditional fields for Access Point
      this.updatePowerSupplyOptions()
      this.setConditionalFields(deviceData)

    } catch (error) {
      console.error("Error in populateFormWithDevice:", error)
      console.error("Stack trace:", error.stack)
      // Fallback to basic field setting
      this.setBasicFormFields(deviceData)
    } finally {
      // Use a more robust approach to ensure form is completely stable
      // Simple synchronous completion
      this.isPopulatingForm = false
      this.lastFormPopulationTime = Date.now()

    }
  }

  // Update conditional sections directly without relying on device controller
  updateConditionalSectionsDirectly(deviceData) {
    const deviceTypeId = deviceData.deviceTypeId
    const isAccessPoint = this.isAccessPointType(deviceTypeId)
    const isSwitch = this.isSwitchType(deviceTypeId)

    const accessPointSection = document.querySelector('[data-device-target="accessPointSections"]')
    const specsSection = document.querySelector('[data-device-target="specsSection"]')

    if (isAccessPoint) {
      if (accessPointSection) {
        accessPointSection.style.display = 'block'
      }
      if (specsSection) {
        specsSection.style.display = 'none'
      }
      this.updatePowerSupplyOptions()
    } else if (isSwitch) {
      if (accessPointSection) {
        accessPointSection.style.display = 'none'
      }
      if (specsSection) {
        specsSection.style.display = 'block'
        this.populateSpecs(deviceTypeId)
      }
    } else {
      if (accessPointSection) {
        accessPointSection.style.display = 'none'
      }
      if (specsSection) {
        specsSection.style.display = 'none'
      }
    }
  }

  // Set conditional fields for Access Point devices
  setConditionalFields(deviceData) {
    const deviceTypeId = deviceData.deviceTypeId
    const isAccessPoint = this.isAccessPointType(deviceTypeId)

    if (isAccessPoint) {
      this.updatePowerSupplyOptions()

      // Set placement field
      if (deviceData.placement) {
        this.setDropdownValue('placement', deviceData.placement)
      } else {
      }

      // Set power supply field (check both naming conventions)
      const powerSupplyId = deviceData.powerSupplyId || deviceData.power_supply_id
      if (powerSupplyId) {
        this.setDropdownValue('power_supply_id', powerSupplyId)
      } else {
      }

      this.updatePowerSupplyOptions()
      // Set power percentage if available (check multiple naming conventions)
      const powerPercentage = deviceData.powerPercentage || deviceData.power_percentage || 75 // Default to 75%
      this.updatePowerSliderUI(powerPercentage)
    } else {
    }
  }

  // Populate hardcoded specs for Switch only (Gateway hides this section)
  populateSpecs(deviceTypeId) {
    const specsContent = document.querySelector('[data-device-target="specsContent"]')
    const section = document.querySelector('[data-device-target="specsSection"]')
    if (!specsContent || !section) return

    // Ensure table skeleton exists
    this._ensureSpecsTemplate(specsContent)

    if (!this.isSwitchType(deviceTypeId)) {
      this._resetSpecsFields(specsContent)
      section.style.display = "none"
      return
    }

    const models = this.getDeviceModelsForType(deviceTypeId)
    const currentModelId = this.getDropdownValue("device_model_id")
    const model = models.find(m => String(m.id || m.value) === String(currentModelId))

    if (!currentModelId || !model) {
      this._resetSpecsFields(specsContent)
      section.style.display = "none"
      return
    }

    const sw = model && (model.gateway_spec || model.gatewaySpec || model.switch_spec || model.switchSpec)
    if (!sw) {
      this._resetSpecsFields(specsContent)
      specsContent.insertAdjacentHTML("beforeend", `<div class="muted">No switch specs available</div>`)
      section.style.display = "block"
      return
    }

    const fmt = (v) => (v === null || v === undefined || Number.isNaN(Number(v))) ? "-" : v
    const fields = {
      ports_10g: sw.ports_10g,
      ports_5g: sw.ports_5g,
      ports_2_5g: sw.ports_2_5g,
      ports_1g: sw.ports_1g,
      poe_ports_bt: sw.poe_ports_bt,
      poe_ports_at: sw.poe_ports_at,
      poe_ports_af: sw.poe_ports_af,
      poe_budget_watts: sw.poe_budget_watts
    }

    Object.entries(fields).forEach(([key, value]) => {
      const cell = specsContent.querySelector(`[data-specs-field="${key}"]`)
      if (cell) cell.textContent = fmt(value)
    })

    section.style.display = "block"
  }

  updatePowerSupplyOptions() {
    const deviceTypeId = this.getDropdownValue('device_type_id')
    const deviceModelId = this.getDropdownValue('device_model_id')
    this.updateAccessPointSpecs(deviceTypeId, deviceModelId)

    const powerSupplyContainer = document.querySelector('#power_supply_id')
    if (!powerSupplyContainer) return

    const options = powerSupplyContainer.querySelectorAll('.dropdown-option[data-value]')
    if (!options.length) return

    let allowed = new Set(['adapter'])

    if (deviceTypeId && deviceModelId) {
      const models = this.getDeviceModelsForType(deviceTypeId)
      const model = models.find(m => String(m.id || m.value) === String(deviceModelId))
      const apSpec = model && (model.ap_spec || model.apSpec)
      const sources = apSpec && Array.isArray(apSpec.power_sources) ? apSpec.power_sources : []
      if (sources.length > 0) {
        allowed = new Set()
        const normalized = sources.map(s => String(s).toLowerCase())
        if (normalized.includes('adapter')) allowed.add('adapter')
        if (normalized.some(s => s.startsWith('802.3'))) allowed.add('poe')
      }
    }

    if (!allowed.size) allowed.add('adapter')

    const selectedValue = this.getDropdownValue('power_supply_id')
    let selectionAllowed = false

    options.forEach(opt => {
      const display = (opt.dataset.display || opt.textContent || '').toLowerCase()
      let optionType = null
      if (display.includes('adapter')) optionType = 'adapter'
      else if (display.includes('poe')) optionType = 'poe'

      const shouldShow = optionType ? allowed.has(optionType) : false
      opt.style.display = shouldShow ? '' : 'none'

      if (selectedValue && opt.dataset.value.toString() === selectedValue.toString() && shouldShow) {
        selectionAllowed = true
      }
    })

    if (selectedValue && !selectionAllowed) {
      this.setDropdownValue('power_supply_id', '', 'Select value')
    }
  }

  updateAccessPointSpecs(deviceTypeId, deviceModelId) {
    const lanPortsTarget = document.querySelector('[data-device-target="apLanPorts"]')
    if (!lanPortsTarget) return

    const typeId = deviceTypeId || this.getDropdownValue('device_type_id')
    if (!this.isAccessPointType(typeId)) {
      lanPortsTarget.textContent = "-"
      return
    }

    const modelId = deviceModelId || this.getDropdownValue('device_model_id')
    if (!modelId) {
      lanPortsTarget.textContent = "-"
      return
    }

    const models = this.getDeviceModelsForType(typeId)
    const model = models.find(m => String(m.id || m.value) === String(modelId))
    const apSpec = model && (model.ap_spec || model.apSpec)
    const lanPorts = apSpec ? (apSpec.lan_ports ?? apSpec.lanPorts) : null
    lanPortsTarget.textContent = (lanPorts === null || lanPorts === undefined || lanPorts === "") ? "-" : lanPorts
  }

  _ensureSpecsTemplate(specsContent) {
    if (specsContent.querySelector('[data-specs-field]')) return
    specsContent.innerHTML = `
        <table class="specs-table">
          <tr><td>Port</td><td>Amount</td></tr>
          <tr><td>10 G</td><td data-specs-field="ports_10g">-</td></tr>
          <tr><td>5 G</td><td data-specs-field="ports_5g">-</td></tr>
          <tr><td>2.5 G</td><td data-specs-field="ports_2_5g">-</td></tr>
          <tr><td>1 G</td><td data-specs-field="ports_1g">-</td></tr>
          <tr><td>PoE 802.3bt</td><td data-specs-field="poe_ports_bt">-</td></tr>
          <tr><td>PoE 802.3at</td><td data-specs-field="poe_ports_at">-</td></tr>
          <tr><td>PoE 802.3af</td><td data-specs-field="poe_ports_af">-</td></tr>
          <tr><td>PoE Budget (W)</td><td data-specs-field="poe_budget_watts">-</td></tr>
        </table>
      `
  }

  _resetSpecsFields(specsContent) {
    specsContent.querySelectorAll('[data-specs-field]').forEach(cell => { cell.textContent = "-" })
    // Remove any muted message if present
    specsContent.querySelectorAll('.muted').forEach(el => el.remove())
  }

  // Synchronous device models loading from preloaded data
  loadDeviceModelsSync(deviceTypeId) {
    if (!deviceTypeId) return

    const deviceModels = this.getDeviceModelsForType(deviceTypeId)

    // Update device model dropdown options directly
    this.updateDeviceModelDropdown(deviceModels)
  }

  // Get device models from preloaded data
  getDeviceModelsForType(deviceTypeId) {

    if (this.hasDeviceModelsMapValue) {
      const models = this.deviceModelsMapValue[deviceTypeId.toString()] || []
      return models
    }

    // Fallback to empty array if no preloaded data

    return []
  }

  // Update device model dropdown with new options (supports SimplifiedDropdownComponent).
  updateDeviceModelDropdown(deviceModels) {

    // Find the device model dropdown container
    const deviceModelContainer = this.element.querySelector('#device_model_id')
    if (!deviceModelContainer) {
      console.error('Device model dropdown container not found')
      return
    }

    // Check if it's SimplifiedDropdownComponent
    const isSimplifiedDropdown = deviceModelContainer.querySelector('[data-simplified-dropdown-target="optionsPanel"]')

    if (isSimplifiedDropdown) {
      this.updateSimplifiedDropdownOptions(deviceModelContainer, deviceModels)
    }
  }

  // Update SimplifiedDropdownComponent options.
  updateSimplifiedDropdownOptions(container, deviceModels) {
    // Get the SimplifiedDropdownController instance
    const dropdownController = this.application.getControllerForElementAndIdentifier(
      container,
      'simplified-dropdown'
    )

    if (!dropdownController) {
      console.error('SimplifiedDropdownController not found for device model dropdown')
      return
    }

    // Use the controller's public updateOptions method.
    dropdownController.updateOptions(deviceModels)
  }



  // Reliable dropdown value setting with error handling
  setDropdownValueReliably(fieldName, value) {
    const success = this.setDropdownValue(fieldName, value)
    if (!success) {
      console.warn(`Failed to set ${fieldName} to ${value}`)
    }
    return success
  }

  // Simplified synchronous form population finalization
  finalizeFormPopulation() {
    // Since operations are now synchronous, immediately mark as complete
    this.isPopulatingForm = false
    this.lastFormPopulationTime = Date.now()

  }

  // Render device models using Turbo Stream (following original pattern)
  renderDeviceModels(deviceTypeId) {
    if (!deviceTypeId) return Promise.resolve()

    return fetch(`/device_types/${deviceTypeId}/device_models`, {
      headers: {
        Accept: "text/vnd.turbo-stream.html"
      }
    })
      .then(response => {
        if (response.ok) {
          return response.text()
        }
        throw new Error("Turbo Stream failed")
      })
      .then(html => {
        Turbo.renderStreamMessage(html)
      })
      .catch(error => {
        console.error('Failed to render device models:', error)
        throw error
      })
  }

  setBasicFormFields(deviceData) {
    // Set device type
    if (deviceData.deviceTypeId) {
      this.setDropdownValue('device_type_id', deviceData.deviceTypeId)
    }

    // Set device model
    if (deviceData.deviceModelId) {
      this.setDropdownValue('device_model_id', deviceData.deviceModelId)
    }

    // Set placement
    if (deviceData.placement) {
      this.setDropdownValue('placement', deviceData.placement)
    }

    // Set power supply
    if (deviceData.powerSupplyId) {
      this.setDropdownValue('power_supply_id', deviceData.powerSupplyId)
    }

    this.updatePowerSupplyOptions()

    if (deviceData.powerPercentage) {
      this.updatePowerSliderUI(deviceData.powerPercentage)
    }
  }


  clearForm() {

    this.clearFormForPopulation()
  }

  clearFormForPopulation() {
    // Clear all dropdown values
    const dropdownFields = ['device_type_id', 'device_model_id', 'placement', 'power_supply_id']

    dropdownFields.forEach(fieldName => {
      // Clear hidden fields
      const hiddenField = document.getElementById(fieldName)
      if (hiddenField) {
        hiddenField.value = ''
      }

      const generalHiddenField = document.getElementById(`device_${fieldName}`)
      if (generalHiddenField) {
        generalHiddenField.value = ''
      }

      // Clear radio buttons
      const radioButtons = document.querySelectorAll(`input[name="device[${fieldName}]"]`)
      radioButtons.forEach(radio => {
        radio.checked = false
      })
    })

    // Clear power percentage
    this.updatePowerSliderUI(100)

    // Clear validation errors
    this.clearValidationErrors()
  }

  // Helper methods for form re-rendering
  setFieldValue(fieldName, value) {
    const hiddenField = document.getElementById(fieldName)
    if (hiddenField) {
      hiddenField.value = value || ''
    }
  }

  updateDropdownSelection(fieldName, value) {
    if (!value) {
      return
    }

    // Find the correct radio button first
    let radioButton
    if (fieldName === 'device_model_id') {
      radioButton = document.querySelector(`input[type="radio"][name*="device_model_id"][value="${value}"]`)
    } else {
      radioButton = document.querySelector(`input[name="device[${fieldName}]"][value="${value}"]`)
    }

    if (radioButton) {
      // Check the radio button
      radioButton.checked = true

      // Update corresponding hidden field
      if (fieldName === 'device_model_id') {
        const hiddenField = document.querySelector(`input[name="device[${fieldName}]"][type="hidden"]`)
        if (hiddenField) {
          hiddenField.value = value
        }
      } else if (fieldName === 'device_type_id') {
        // For device_type_id, update the direct hidden field
        const hiddenField = document.getElementById('device_type_id')
        if (hiddenField) {
          hiddenField.value = value
        }
      }

      // Trigger change event for radio button
      const changeEvent = new Event('change', { bubbles: true })
      radioButton.dispatchEvent(changeEvent)

      // Simulate user clicking the corresponding label to update display text
      // Find the label that corresponds to this radio button value
      let targetLabel = null

      if (fieldName === 'device_model_id') {
        // For device model, find label in the dropdown list
        targetLabel = document.querySelector(`#device_model_id_dropdown_list label[for="${value}"]`)
      } else {
        // For device_type_id, find label immediately (don't wait)
        if (fieldName === 'device_type_id') {
          const radioBtnId = radioButton.id
          if (radioBtnId) {
            targetLabel = document.querySelector(`label[for="${radioBtnId}"]`) ||
              document.querySelector(`label[for="${value}"]`)
          }
        } else {
          // For placement and power_supply_id, wait for conditional sections to render then find the label
          setTimeout(() => {
            let container = null
            if (fieldName === 'placement') {
              container = document.querySelector('#placement')
            } else if (fieldName === 'power_supply_id') {
              container = document.querySelector('#power_supply_id')
            }

            if (container) {
              const labels = container.querySelectorAll('label')
              let matchingLabel = null

              for (const label of labels) {
                const valueStr = String(value)
                if (label.getAttribute('for') === valueStr ||
                  label.getAttribute('for') === String(value) ||
                  label.getAttribute('for') === radioButton.id ||
                  label.textContent.trim().toLowerCase() === valueStr.toLowerCase() ||
                  label.textContent.trim().toLowerCase() === (valueStr.charAt(0).toUpperCase() + valueStr.slice(1)).toLowerCase()) {
                  matchingLabel = label
                  break
                }
              }

              if (matchingLabel) {
                const displayElement = document.querySelector(`#${fieldName}_dropdown_input_value`)
                if (displayElement) {
                  displayElement.innerHTML = matchingLabel.innerHTML
                }
              }
            }
          }, 50)

          // Don't set targetLabel for delayed updates
          targetLabel = null
        }
      }

      if (targetLabel) {
        // Simulate the showSelectedValue logic from dropdown_input_controller
        const displayElement = document.querySelector(`#${fieldName}_dropdown_input_value`)
        if (displayElement) {
          displayElement.innerHTML = targetLabel.innerHTML
        }
      }
    } else {
      // Retry for device_model_id (async content)
      if (fieldName === 'device_model_id') {
        setTimeout(() => {
          const retryRadioButton = document.querySelector(`input[type="radio"][name*="device_model_id"][value="${value}"]`)
          if (retryRadioButton) {
            retryRadioButton.checked = true
            const targetLabel = document.querySelector(`#device_model_id_dropdown_list label[for="${value}"]`)
            if (targetLabel) {
              const displayElement = document.querySelector(`#${fieldName}_dropdown_input_value`)
              if (displayElement) {
                displayElement.innerHTML = targetLabel.innerHTML
              }
            }
          }
        }, 200)
      }
    }
  }

  clearDeviceModelSelection() {
    // Clear existing device model selections
    const deviceModelRadios = document.querySelectorAll(`input[name="device[device_model_id]"]`)
    deviceModelRadios.forEach(radio => {
      radio.checked = false
    })

    // Clear hidden field
    const hiddenField = document.getElementById('device_model_id')
    if (hiddenField) {
      hiddenField.value = ''
    }
  }


  updateConditionalSectionsByTypeId(deviceTypeId) {
    // Try multiple ways to find the device controller
    let deviceController = null

    // Method 1: Look for controller on form element
    const formElement = document.querySelector('[data-controller*="device "], [data-controller*="device-form"]')
    if (formElement) {
      deviceController = this.application.getControllerForElementAndIdentifier(formElement, 'device')
    }

    // Method 2: Look for controller on floor plan element
    if (!deviceController) {
      const floorPlanElement = document.querySelector('.floor-plan-img[data-controller*="device"]')
      if (floorPlanElement) {
        deviceController = this.application.getControllerForElementAndIdentifier(floorPlanElement, 'device')
      }
    }

    // Method 3: Direct search
    if (!deviceController) {
      const deviceControllerElement = document.querySelector('[data-controller*="device"]:not([data-controller*="device-form"]):not([data-controller*="device-placement"]):not([data-controller*="device-icon"])')
      if (deviceControllerElement) {
        deviceController = this.application.getControllerForElementAndIdentifier(deviceControllerElement, 'device')
      }
    }

    if (deviceController) {
      if (typeof deviceController._updateConditionalSectionsByTypeId === 'function') {
        deviceController._updateConditionalSectionsByTypeId(deviceTypeId)
      } else {
        const deviceTypeName = this.getDeviceTypeNameFromId(deviceTypeId)
        deviceController._updateConditionalSectionsByName(deviceTypeName)
      }
    } else {
    }
  }

  updateConditionalSectionsForDevice(device) {
    // Trigger the device controller's conditional sections update
    const deviceController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller="device"]'),
      'device'
    )

    if (deviceController) {
      if (typeof deviceController._updateConditionalSectionsByTypeId === 'function') {
        deviceController._updateConditionalSectionsByTypeId(device.deviceTypeId)
      } else {
        const deviceTypeName = this.getDeviceTypeNameFromId(device.deviceTypeId)
        deviceController._updateConditionalSectionsByName(deviceTypeName)
      }
    } else {
    }
  }

  getDeviceTypeNameFromId(deviceTypeId) {
    if (!deviceTypeId) return null

    const dropdown = document.querySelector('#device_type_id')
    if (dropdown) {
      // Try SimplifiedDropdownComponent structure first
      const option = dropdown.querySelector(`[data-value="${deviceTypeId}"]`)
      if (option) {
        return option.textContent.trim()
      }

      // Fallback to legacy structure
      const legacyOption = dropdown.querySelector(`label[id="${deviceTypeId}"]`)
      if (legacyOption) {
        return legacyOption.textContent.trim()
      }
    }

    // Fallback: try to get from the current display text
    const displayElement = document.querySelector('#device_type_id [data-simplified-dropdown-target="displayText"]')
    if (displayElement && displayElement.textContent.trim() !== 'Select value') {
      return displayElement.textContent.trim()
    }

    return null
  }

  normalizeTypeIds(raw) {
    if (!Array.isArray(raw)) return []
    return raw.map(id => String(id))
  }

  getAccessPointTypeIds() {
    if (this._accessPointTypeIds) return this._accessPointTypeIds
    const raw = this.hasAccessPointTypeIdsValue ? this.accessPointTypeIdsValue : []
    this._accessPointTypeIds = this.normalizeTypeIds(raw)
    return this._accessPointTypeIds
  }

  getSwitchSpecTypeIds() {
    if (this._switchSpecTypeIds) return this._switchSpecTypeIds
    const raw = this.hasSwitchSpecTypeIdsValue ? this.switchSpecTypeIdsValue : []
    this._switchSpecTypeIds = this.normalizeTypeIds(raw)
    return this._switchSpecTypeIds
  }

  isAccessPointType(deviceTypeId) {
    if (!deviceTypeId) return false
    return this.getAccessPointTypeIds().includes(String(deviceTypeId))
  }

  isSwitchSpecType(deviceTypeId) {
    if (!deviceTypeId) return false
    return this.getSwitchSpecTypeIds().includes(String(deviceTypeId))
  }

  isSwitchType(deviceTypeId) {
    if (!this.isSwitchSpecType(deviceTypeId)) return false
    const typeName = (this.getDeviceTypeNameFromId(deviceTypeId) || "").toLowerCase()
    return typeName.includes("switch") && !typeName.includes("gateway")
  }

  updateDeviceSpecificFields(deviceData) {
    // Set placement
    if (deviceData.placement) {
      this.setDropdownValue('placement', deviceData.placement)
      this.updateDropdownSelection('placement', deviceData.placement)
    }

    // Set power supply
    if (deviceData.powerSupplyId) {
      this.setDropdownValue('power_supply_id', deviceData.powerSupplyId)
      this.updateDropdownSelection('power_supply_id', deviceData.powerSupplyId)
    }

    this.updatePowerSupplyOptions()

    if (deviceData.powerPercentage) {
      this.updatePowerSliderUI(deviceData.powerPercentage)
    }
  }

  updatePowerSliderUI(value) {
    const powerSlider = document.getElementById('power_percentage_slider')
    if (!powerSlider) {
      return
    }

    const normalizedValue = Math.max(0, Math.min(100, Number(value) || 0))
    powerSlider.value = normalizedValue

    const powerDisplay = document.getElementById('power_percentage_display')
    if (powerDisplay) {
      powerDisplay.textContent = `${normalizedValue}%`
    }

    // Ensure reference values refresh via device controller
    powerSlider.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // Helper method to dispatch custom events
  dispatch(eventName, detail = {}) {
    const event = new CustomEvent(`device:form:${eventName}`, { detail })
    document.dispatchEvent(event)
  }
}
